/**
 * Reference-layer catalog from GET /v1/tenant (`layers`).
 *
 * The backend lists every read-only layer this jurisdiction's map can show
 * (see backend/app/layers.py): staff-uploaded district maps (current,
 * drafts, older maps), legislative districts, census geography (counties,
 * cities, census-designated places, tracts) and tribal areas. The map and
 * the Layers control render whatever is listed -- nothing here is
 * hard-coded per layer.
 */
import type { TenantConfig } from './api';

export type LayerGroup = 'district_maps' | 'legislative' | 'census' | 'tribal';

/** Display order; names are translated (`layers.groups.<group>`). */
export const LAYER_GROUPS: LayerGroup[] = ['district_maps', 'legislative', 'census', 'tribal'];

export interface LayerSource {
  type: 'pmtiles' | 'reference';
  /** PMTiles archive URL (pmtiles). */
  url: string | null;
  /** Vector layer inside the archive (pmtiles). */
  sourceLayer: string | null;
  /** Point layer with one label per feature (pmtiles); labels fall back to the polygons. */
  labelLayer: string | null;
  /** District-map slug from GET /v1/tenant/reference-layers (reference). */
  slug: string | null;
}

export interface LayerStyle {
  color: string;
  width: number;
  /** MapLibre line-dasharray (in line widths); null = solid. */
  dash: number[] | null;
  opacity: number;
}

export interface LayerDef {
  id: string;
  group: LayerGroup;
  label: string;
  description: string;
  source: LayerSource;
  style: LayerStyle;
  /** Feature properties tried in order for the label / hover name. */
  labelProps: string[];
  /** MapLibre filter expression, e.g. cities vs CDPs in one archive. */
  filter: unknown[] | null;
  minZoom: number | null;
  labelMinZoom: number | null;
  defaultVisible: boolean;
  attribution: string | null;
  /** District maps: old_districts | draft | historical. */
  mapKind: string | null;
  /** District maps: ISO date (YYYY-MM-DD). */
  effectiveDate: string | null;
  /** District maps: the staff-written description. */
  note: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function toLayer(value: unknown): LayerDef | null {
  if (!isRecord(value) || !isRecord(value.source) || !isRecord(value.style)) return null;
  const id = str(value.id);
  const group = value.group as LayerGroup;
  if (!id || !LAYER_GROUPS.includes(group)) return null;
  const source = value.source;
  const type = source.type === 'reference' ? 'reference' : source.type === 'pmtiles' ? 'pmtiles' : null;
  if (!type) return null;
  const parsed: LayerDef = {
    id,
    group,
    label: str(value.label) ?? id,
    description: str(value.description) ?? '',
    source: {
      type,
      url: str(source.url),
      sourceLayer: str(source.source_layer),
      labelLayer: str(source.label_layer),
      slug: str(source.slug),
    },
    style: {
      color: str(value.style.color) ?? '#57534E',
      width: num(value.style.width) ?? 1.5,
      dash: Array.isArray(value.style.dash) ? value.style.dash.filter((n): n is number => typeof n === 'number') : null,
      opacity: num(value.style.opacity) ?? 0.9,
    },
    labelProps: Array.isArray(value.label_props)
      ? value.label_props.filter((p): p is string => typeof p === 'string')
      : ['name'],
    filter: Array.isArray(value.filter) ? value.filter : null,
    minZoom: num(value.min_zoom),
    labelMinZoom: num(value.label_min_zoom),
    defaultVisible: value.default_visible === true,
    attribution: str(value.attribution),
    mapKind: str(value.map_kind),
    effectiveDate: str(value.effective_date),
    note: str(value.note),
  };
  if (type === 'pmtiles' && (!parsed.source.url || !parsed.source.sourceLayer)) return null;
  if (type === 'reference' && !parsed.source.slug) return null;
  return parsed;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * A layer's name and description in the reader's language. Built-in layers
 * (legislative, census, tribal) have translations keyed by id; district maps
 * keep the name staff gave them, with the date phrased per language.
 */
export function layerText(
  layer: LayerDef,
  t: Translate,
  language: string,
): { label: string; description: string } {
  if (layer.group !== 'district_maps') {
    return {
      label: t(`layers.builtin.${layer.id}.label`, { defaultValue: layer.label }),
      description: t(`layers.builtin.${layer.id}.description`, { defaultValue: layer.description }),
    };
  }
  const kind = layer.mapKind ?? 'historical';
  const parts = [layer.note ?? t(`layers.mapKind.${kind}`, { defaultValue: layer.description })];
  if (layer.effectiveDate) {
    const d = new Date(`${layer.effectiveDate}T00:00:00`);
    const date = Number.isNaN(d.getTime())
      ? layer.effectiveDate
      : d.toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' });
    parts.push(t(`layers.mapDate.${kind}`, { date }));
  }
  return { label: layer.label, description: parts.join(' ') };
}

/** The tenant's layer catalog (defensive: GET /v1/tenant is a network boundary). */
export function tenantLayers(tenant: TenantConfig): LayerDef[] {
  const raw = (tenant as { layers?: unknown }).layers;
  if (!Array.isArray(raw)) return [];
  const out: LayerDef[] = [];
  for (const item of raw) {
    const layer = toLayer(item);
    if (layer) out.push(layer);
  }
  return out;
}
